import { AnalyticsEventNameType } from "@coeff/api";
import * as Sentry from "@sentry/react";
import React, { useCallback, useEffect, useRef, useState } from "react";

import { useTrack } from "../../utils";

import { AutoScaleText } from "./AutoScaleText";
import { BLOCK_POPOVER_CLASS } from "./blockDescriptionHtml";
import { DashboardWindow, normalizeSlicerStateForRealm, RenderFunction } from "./dashboardHelpers";
import { useReadableIconColorClass } from "./metricCardIconColor";
import { applyMetricTextContrast } from "./metricCardTextContrast";
import { useTheme } from "./themes";
import { ErrorStateDetails, renderErrorFallback } from "./useBlockRender";
import { CleanupFunction, SlicerState } from "./useDashboardRuntime";
import { useIsMobile } from "./useIsMobile";
import { useWebDashboardViewerContext } from "./WebDashboardViewerContext";

/** Module scope so add/removeEventListener always see the same function identity. */
const stopEventPropagation = (e: Event) => e.stopPropagation();

/** How long after a contrast pass Tailwind stylesheet updates still trigger a re-run. */
const STYLESHEET_WATCH_MS = 2000;

/**
 * Whether a head mutation changed a `<style>` element — Tailwind's compiled CSS, or a block
 * injecting its own. Other head churn (scripts, meta, links) can't change the card's colors.
 * Compared by nodeName, not instanceof: the nodes belong to the dashboard iframe's realm.
 */
const isStyleNode = (node: Node | null): boolean => node?.nodeName === "STYLE";
const touchesStyleElement = (mutation: MutationRecord): boolean =>
  isStyleNode(mutation.target) ||
  isStyleNode(mutation.target.parentNode) ||
  Array.from(mutation.addedNodes).some(isStyleNode);

interface MetricCardBlockProps {
  blockId: string;
  label: string;
  icon: string;
  /**
   * Authored Tailwind color class, set only when a user asked for a specific color. Unset icons
   * render in the theme's card foreground.
   */
  iconColor?: string | null;
  /**
   * Spec fields `show_icon` / `show_comparison`: user-controlled display toggles from the manual
   * block editor. Unset (null/undefined) means shown — only an explicit false hides.
   */
  showIcon?: boolean | null;
  showComparison?: boolean | null;
  renderFunctionJs: string;
  slicerState: SlicerState;
  targetWindow: DashboardWindow;
  idSuffix?: string;
  blockType: string;
  // BE-provided pre-render error attribution (e.g. missing source data). When
  // set, the error fallback shows this string verbatim in place of the generic
  // "Unable to display metric card" title and the action button switches from
  // "Fix this" (auto-fix) to "Learn more" (explain in chat).
  errorState?: string | null;
  // Companion to `errorState`: open dict of structured attribution details
  // (currently `{ tabs?: string[] }` for source tabs, rendered as a tooltip).
  errorStateDetails?: ErrorStateDetails | null;
  onRendered?: (blockId: string) => void;
  onRenderStart?: (blockId: string) => void;
  onRenderComplete?: (blockId: string) => void;
  /** Wired to the "Fix this" button shown in the runtime-JS-error fallback. */
  onFixBlock?: (blockId: string, errorMessage: string) => void;
  /** Wired to the "Learn more" button shown in the BE-attributed fallback. */
  onExplainBlockInChat?: (
    blockId: string,
    errorMessage?: string,
    errorStateDetails?: ErrorStateDetails | null
  ) => void;
}

/**
 * True for a period label that covers all time rather than a real reporting window.
 *
 * Year 9999 is never a reporting period — it is the max-date placeholder a query falls back to
 * when nothing bounds the date column, which surfaces as labels like "Jan 1900 – Dec 9999".
 * Deliberately narrow: a label with a sentinel start but a real end ("Jan 1900 – Aug 2026") still
 * describes a range the reader can act on, so it is left alone.
 */
function isAllTimePeriodLabel(text: string): boolean {
  return /\b9999\b/.test(text) || /^all[\s-]*time$/i.test(text);
}

/**
 * True for a comparison the render JS could not compute. The block manager contract tells render
 * JS to write "—" in `metric-status-neutral` when the prior period is too small to give a
 * meaningful % change, so a bare dash means "no comparison", not "a comparison worth showing".
 */
function isEmptyComparisonLabel(text: string): boolean {
  return text === "" || /^[-–—]$/.test(text);
}

/**
 * Show `.metric-status` only when it carries a real comparison.
 *
 * Two independent reasons to hide, and they must not be conflated: `show_comparison === false` is
 * a persisted user preference, while a bare dash is a property of the current data. Using the
 * preference to hide today's dash would also hide a genuine comparison later, once the prior
 * period has enough data — so the dash collapses on its own and the toggle stays a separate,
 * deliberate choice. Set both ways so a later render with a real value shows the line again.
 */
function applyMetricStatusVisibility(
  cardElement: HTMLElement,
  showComparison?: boolean | null
): void {
  const statusEl = cardElement.querySelector<HTMLElement>(".metric-status");
  if (!statusEl) {
    return;
  }
  const hide =
    showComparison === false || isEmptyComparisonLabel(statusEl.textContent?.trim() ?? "");
  statusEl.style.display = hide ? "none" : "";
}

/**
 * Hide `.metric-date` when the render JS wrote an all-time period, rather than showing a
 * fabricated range. The element is written by the block's own render JS (it is not spec-owned),
 * so this runs after each render and sets visibility BOTH ways — a later render that produces a
 * real period shows the line again.
 */
function applyAllTimeDateVisibility(cardElement: HTMLElement): void {
  const dateEl = cardElement.querySelector<HTMLElement>(".metric-date");
  if (!dateEl) {
    return;
  }
  dateEl.style.display = isAllTimePeriodLabel(dateEl.textContent?.trim() ?? "") ? "none" : "";
}

export const MetricCardBlock: React.FC<MetricCardBlockProps> = ({
  blockId,
  label,
  icon,
  iconColor,
  showIcon,
  showComparison,
  renderFunctionJs,
  slicerState,
  targetWindow,
  idSuffix = "",
  blockType,
  errorState,
  errorStateDetails,
  onRendered,
  onRenderStart,
  onRenderComplete,
  onFixBlock,
  onExplainBlockInChat,
}) => {
  const track = useTrack();
  const { themeSignature } = useTheme();
  const { dashboardId, dashboardName, spreadsheetId, slicerNames } = useWebDashboardViewerContext();
  const cardRef = useRef<HTMLDivElement>(null);
  // Read by the post-render visibility pass, whose effect closure predates any toggle made while
  // that render was in flight.
  const showComparisonRef = useRef(showComparison);
  // Read by the contrast pass so an icon color edit does not change that callback's identity,
  // which would re-run the render effect (and its SQL) that depends on it.
  const iconColorRef = useRef(iconColor);
  iconColorRef.current = iconColor;
  const cleanupRef = useRef<CleanupFunction | null>(null);
  const hasCalledOnRenderedRef = useRef(false);
  const isFirstRenderRef = useRef(true);
  const isMobile = useIsMobile(targetWindow);

  const fullBlockId = `block_${blockId}${idSuffix ? `_${idSuffix}` : ""}`;

  // Bumped after each contrast pass so the icon is re-measured against the surface render JS
  // just painted, not the theme surface it mounted on.
  const [surfaceVersion, setSurfaceVersion] = useState(0);
  const iconColorClassName = useReadableIconColorClass(
    iconColor,
    cardRef,
    `${themeSignature}:${surfaceVersion}`
  );

  /**
   * Readability pass over the card's text (ENG-8813), run after render JS has painted the card.
   *
   * Deferred rather than run inline: render JS colors a card with Tailwind classes, and the CDN
   * build compiles a class it has not seen before from a MutationObserver callback. The order of
   * that callback against the render promise's continuation is not guaranteed, so measuring
   * immediately could read the card before its new background exists. Once the CDN compiler is
   * up, the stylesheet is in place by the next animation frame — but a card that renders while the
   * compiler is still initialising gets its classes compiled later than that. So after the first
   * pass the document head is watched for a short window and the pass re-runs (next frame) after
   * each stylesheet update. Re-running is safe: the pass restores the authored color before it
   * measures.
   *
   * `frames` is how many animation frames to wait. A theme switch needs two: applyTheme sets the
   * card and token CSS variables inside `document.startViewTransition`, whose update callback
   * runs in the same frame's rendering steps AFTER animation-frame callbacks, so a single frame
   * can still measure the previous theme's surface.
   */
  const scheduleTextContrastPass = useCallback(
    (isCancelled: () => boolean, frames = 1) => {
      const run = () => {
        if (isCancelled() || !cardRef.current) {
          return false;
        }
        applyMetricTextContrast(cardRef.current);
        // Only an authored icon color is re-measured; the theme-foreground default never needs
        // it, and the bump is a synchronous re-render plus a layout probe per card.
        if (iconColorRef.current) {
          setSurfaceVersion(version => version + 1);
        }
        return true;
      };
      const watchStylesheetUpdates = () => {
        const head = targetWindow.document.head;
        if (!head) {
          return;
        }
        let rerunPending = false;
        const observer = new MutationObserver(mutations => {
          if (isCancelled()) {
            observer.disconnect();
            return;
          }
          if (rerunPending || !mutations.some(touchesStyleElement)) {
            return;
          }
          rerunPending = true;
          targetWindow.requestAnimationFrame(() => {
            rerunPending = false;
            if (!run()) {
              observer.disconnect();
            }
          });
        });
        observer.observe(head, { childList: true, subtree: true, characterData: true });
        targetWindow.setTimeout(() => observer.disconnect(), STYLESHEET_WATCH_MS);
      };
      const tick = (remaining: number) =>
        targetWindow.requestAnimationFrame(() => {
          if (remaining > 1) {
            tick(remaining - 1);
          } else if (run()) {
            watchStylesheetUpdates();
          }
        });
      tick(frames);
    },
    [targetWindow]
  );

  /**
   * Keeps the mobile label's tap to itself.
   *
   * The card is a drill-down target: block render JS binds a click listener directly to the card
   * element and calls window.setBottomDrawerContent, so a tap anywhere on the card — the label
   * included — opens the drill-down drawer. Left alone, tapping the label fires both, opening the
   * drawer with the reveal popover stranded behind it and still open once the drawer closes.
   *
   * Bound to the <details>, NOT the <summary>. The revealed popover is a SIBLING of the summary
   * inside the details, so a summary-only listener leaves the popover itself unguarded: tapping the
   * revealed text bubbled to the card and opened the drill-down, while the outside-tap hook
   * correctly left the popover open because the tap was inside the disclosure — the drawer and a
   * live popover at once, which is the state this exists to prevent. The details wraps both, so one
   * listener covers the whole disclosure.
   *
   * This has to be a NATIVE listener, not React's `onClick`. React 17 delegates from the tree's root
   * container, so the card's own native listener — bound closer to the target — runs first during
   * bubbling and has already opened the drawer by the time a React handler could call
   * stopPropagation. Verified: the React version left the drawer opening anyway. A native listener
   * on the details still sits deeper than the card's, so it runs first and genuinely stops the
   * bubble.
   *
   * Only `click` is stopped. `pointerdown` must keep bubbling to the document, because that is what
   * useCloseBlockPopoversOnOutsideTap listens for, and preventDefault is never called so the
   * <details> still toggles natively. Cards with no drill-down listener are unaffected.
   *
   * A CALLBACK REF, not useRef plus an effect. The card's root element carries
   * `key={fullBlockId + renderFunctionJs}`, so a new render function discards that DOM subtree and
   * builds a fresh one — but the COMPONENT never unmounts, since the key is on the returned element
   * rather than on <MetricCardBlock> itself. An effect keyed on [isMobile] therefore would not re-run:
   * React would repoint the ref at the new <details> while the listener stayed on the discarded node,
   * silently restoring the double-fire (reveal plus drill-down) after any block edit. A callback ref
   * runs on every attach and detach whatever triggered it, so the listener always tracks the live
   * node. Stable via useCallback so it is not torn down and rebuilt on unrelated renders.
   */
  const labelDisclosureRef = useRef<HTMLDetailsElement | null>(null);
  const attachLabelDisclosure = useCallback((node: HTMLDetailsElement | null) => {
    labelDisclosureRef.current?.removeEventListener("click", stopEventPropagation);
    labelDisclosureRef.current = node;
    node?.addEventListener("click", stopEventPropagation);
  }, []);

  useEffect(() => {
    if (!cardRef.current) {
      return;
    }

    let cancelled = false;

    const executeRender = async () => {
      if (!isFirstRenderRef.current) {
        onRenderStart?.(blockId);
      }
      isFirstRenderRef.current = false;

      // Cleanup previous render
      if (cleanupRef.current) {
        try {
          await cleanupRef.current();
        } catch (error) {
          targetWindow.console.error(`Cleanup error for ${fullBlockId}:`, error);
        }
        cleanupRef.current = null;
      }

      if (cancelled) {
        return;
      }

      // Pre-render short-circuit: if the BE has already attributed a failure
      // (e.g. missing source data) via `block.error_state`, skip executing
      // `renderFunctionJs` entirely and render the fallback DOM directly. This
      // ensures the BE-attributed message is displayed even when the render
      // function would not have thrown. Default `errorState === undefined`
      // preserves the legacy try/catch path unchanged.
      if (errorState) {
        if (cardRef.current && !cancelled) {
          renderErrorFallback(
            cardRef.current,
            blockType,
            blockId,
            errorState,
            label,
            onFixBlock,
            errorState,
            onExplainBlockInChat,
            errorStateDetails
          );
        }

        if (!cancelled && !hasCalledOnRenderedRef.current) {
          hasCalledOnRenderedRef.current = true;
          onRendered?.(blockId);
        }
        if (!cancelled) {
          onRenderComplete?.(blockId);
        }

        // Emit per-render completion (failure) for screenshot/load coordination,
        // mirroring the equivalent short-circuit in useBlockRender.ts. Without
        // this, BE-attributed metric-card errors don't signal load-complete
        // to WebDashboardViewer's listener.
        if (!cancelled) {
          try {
            const CustomEventCtor = (targetWindow as any).CustomEvent || CustomEvent;
            const ev = new CustomEventCtor("coeff:block_render_complete", {
              detail: { blockId, ok: false, error: errorState },
            });
            targetWindow.dispatchEvent(ev);
            targetWindow.document.dispatchEvent(ev);
          } catch (e) {
            // Ignore.
          }
        }

        return;
      }

      try {
        // Evaluate the render function in the context of the iframe window
        // Strip trailing semicolon if present (function expressions shouldn't have semicolons when wrapped in return)
        const cleanedRenderFunctionJs = renderFunctionJs.trim().replace(/;\s*$/, "");
        // eslint-disable-next-line no-new-func
        const sourceUrl = `${encodeURIComponent(blockId)}__render_function_js`;
        const renderFn = new targetWindow.Function(
          `return (${cleanedRenderFunctionJs});\n//# sourceURL=${sourceUrl}\n`
        )() as RenderFunction;
        const runSQL = targetWindow.runSQL;
        if (!runSQL) {
          throw new Error("runSQL is not initialized");
        }
        const cleanup = await renderFn(
          cardRef.current!,
          normalizeSlicerStateForRealm(slicerState, targetWindow),
          runSQL
        );
        if (cleanup && !cancelled) {
          cleanupRef.current = cleanup;
        }
        if (!cancelled && cardRef.current) {
          applyAllTimeDateVisibility(cardRef.current);
          applyMetricStatusVisibility(cardRef.current, showComparisonRef.current);
          scheduleTextContrastPass(() => cancelled);
        }
        if (!cancelled && !hasCalledOnRenderedRef.current) {
          hasCalledOnRenderedRef.current = true;
          onRendered?.(blockId);
        }
        if (!cancelled) {
          onRenderComplete?.(blockId);
        }
      } catch (error) {
        targetWindow.console.error(`Render error for ${fullBlockId}:`, error);
        const errorMessage = error instanceof Error ? error.message : String(error);

        Sentry.captureException(error, {
          tags: { feature: "web_dashboard", errorType: "block_render" },
          extra: {
            dashboardId,
            blockId,
            blockType,
            blockTitle: label,
            dashboardName,
            spreadsheetId,
          },
        });

        track(AnalyticsEventNameType.VibeDashBlockRenderingFailed, {
          vibe_dash_id: dashboardId,
          vibe_dash_block_id: blockId,
          vibe_dash_block_type: blockType,
          vibe_dash_block_title: label,
          vibe_dash_slicers: slicerNames,
          error_message: errorMessage,
          ss_id: spreadsheetId,
          vibe_dash_name: dashboardName,
        });

        if (!cancelled && cardRef.current) {
          renderErrorFallback(
            cardRef.current,
            blockType,
            blockId,
            errorMessage,
            label,
            onFixBlock,
            errorState,
            onExplainBlockInChat,
            errorStateDetails
          );
        }

        // Still notify on failure so the load event isn't blocked by a bad block.
        if (!cancelled && !hasCalledOnRenderedRef.current) {
          hasCalledOnRenderedRef.current = true;
          onRendered?.(blockId);
        }
        if (!cancelled) {
          onRenderComplete?.(blockId);
        }
      }
    };

    executeRender();

    return () => {
      cancelled = true;
      if (cleanupRef.current) {
        cleanupRef.current();
        cleanupRef.current = null;
      }
    };
    // Mirrors useBlockRender.ts: callback refs (onFixBlock, onExplainBlockInChat,
    // onRendered, onRenderStart, onRenderComplete) are intentionally omitted to
    // avoid re-running the render effect on every parent re-render that creates
    // new function identities. Block label/icon/iconColor are static per block
    // and don't drive the render function.
  }, [
    fullBlockId,
    renderFunctionJs,
    slicerState,
    targetWindow,
    track,
    dashboardId,
    blockType,
    errorState,
    scheduleTextContrastPass,
  ]); // eslint-disable-line react-hooks/exhaustive-deps

  // A theme switch repaints the card and re-tokens the muted/semantic text under it, so the text
  // is measured again. Crossing the mobile breakpoint remounts `.metric-label` (see the JSX
  // below) with no correction on the fresh node, so that re-measures too. Skipped on mount: the
  // render pass above covers the first paint.
  const isFirstThemeRef = useRef(true);
  useEffect(() => {
    if (isFirstThemeRef.current) {
      isFirstThemeRef.current = false;
      return;
    }
    let cancelled = false;
    scheduleTextContrastPass(() => cancelled, 2);
    return () => {
      cancelled = true;
    };
  }, [themeSignature, isMobile, scheduleTextContrastPass]);

  // Re-apply when the preference changes. The render effect above deliberately does not depend on
  // showComparison — re-running it would re-issue the block's queries just to show or hide a row.
  // That also means its closure holds a stale preference, so the ref is what the post-render pass
  // reads: a renderFn still in flight when the user toggles would otherwise resolve afterwards and
  // restore the old visibility.
  useEffect(() => {
    showComparisonRef.current = showComparison;
    if (cardRef.current) {
      applyMetricStatusVisibility(cardRef.current, showComparison);
    }
  }, [showComparison]);

  return (
    <div
      ref={cardRef}
      id={fullBlockId}
      // Include renderFunctionJs in the key to force a re-render when the render function changes.
      // This prevents the previous render function's DOM updates from persisting.
      key={fullBlockId + renderFunctionJs}
      // Remove default padding with p-0, so we can use CSS container-based queries to adjust
      // content padding.
      className="themed-dashboard-card p-0 @container"
    >
      <div
        className={
          "flex flex-col justify-start text-left w-full h-full " + "p-2 @[130px]:p-4 @[160px]:p-5"
        }
      >
        {/* Anchor label and icon to the top so metric card icons in the same row align.

            `block-title-row` is not decoration: two behaviours are already keyed to that class and
            come for free rather than needing their own plumbing — the <summary> marker reset in
            WebDashboardViewer's iframe stylesheet, and outside-tap dismissal, whose delegated
            listener queries `.block-title-row details[open]`. `max-sm:relative` is the positioned
            ancestor the absolute popover resolves against, exactly as the chart/table title rows
            do. */}
        <div className="block-title-row max-sm:relative flex items-start justify-between gap-2 mt-1">
          {/* `max-sm:truncate` holds the label to one line on phones (matching chart and data-table
              titles). At the ~161px of a two-per-row mobile KPI card, labels like "Avg Revenue per
              Account" wrapped to two lines and pushed the value down, so cards in the same row no
              longer lined up. `min-w-0` is required alongside it: as a flex child the label would
              otherwise refuse to shrink below its text width and never ellipsize. Desktop keeps
              wrapping — at 308px it does not trigger, and a long label stays fully readable.

              Truncating made the tail unreadable with no way back, so on mobile the label doubles
              as a tap-to-reveal disclosure, the same pattern chart and data-table titles use.

              ONE `.metric-label` AT ANY WIDTH, deliberately. renderBlockTitleHtml emits two copies
              of the title — one per breakpoint — because an innerHTML string cannot branch on the
              viewport. This component can, and must: `.metric-label` is a contract class that
              legacy render JS may still write to via querySelector, which matches only the FIRST
              copy, so two copies would leave one breakpoint showing the JS text and the other the
              spec text. Branching on isMobile keeps exactly one in the DOM.

              Consequence worth knowing: crossing 640px remounts the label, so such a legacy card
              reverts to the spec label until its render JS next runs — the render effect's deps do
              not include isMobile. Harmless where the spec owns the label, which is the rule. */}
          {isMobile ? (
            <details ref={attachLabelDisclosure} name={fullBlockId} className="min-w-0">
              {/* See labelDisclosureRef's effect: clicks anywhere in this disclosure — the label
                  and the revealed popover alike — are stopped natively, so revealing never also
                  triggers the card's drill-down. */}
              <summary className="block min-w-0">
                <div
                  className="metric-label font-medium text-card-muted-foreground min-w-0 truncate"
                  style={{ fontSize: "clamp(0.4rem, 9cqi, 1rem)" }}
                >
                  {label}
                </div>
              </summary>
              <div className={BLOCK_POPOVER_CLASS}>{label}</div>
            </details>
          ) : (
            <div
              className="metric-label font-medium text-card-muted-foreground"
              style={{ fontSize: "clamp(0.4rem, 9cqi, 1rem)" }}
            >
              {label}
            </div>
          )}
          {/* KPI icons are desktop-only. The `@[130px]:!inline` container query below is not enough
              on its own: mobile lays KPI cards out two-per-row at ~161px, which clears that 130px
              threshold, so the icon showed up on phones anyway. Skip rendering it rather than adding
              a competing `max-sm:!hidden` — both would be `!important`, so which one won would come
              down to stylesheet order. Dropping it also hands the label the full card width.

              The colour is not decided here: useReadableIconColorClass keeps the block's authored
              icon_color where it clears WCAG non-text contrast against the measured card surface,
              and degrades to the theme foreground only where it does not. */}
          {/* `show_icon === false` keeps the span in the DOM but drops the container-query
              reveal, so it stays `!hidden` at every width. Removing the element instead would
              crash legacy render JS that still does `querySelector('.metric-icon')`. */}
          {!isMobile && (
            <span
              className={`metric-icon material-icons ${iconColorClassName} !hidden ${
                showIcon === false ? "" : "@[130px]:!inline"
              }`}
              style={{ fontSize: "clamp(0.1rem, 12cqi, 1.9rem)" }}
            >
              {icon}
            </span>
          )}
        </div>
        <AutoScaleText innerClassName="metric-value font-semibold text-[2.3rem]" data-sentry-mask>
          -
        </AutoScaleText>
        {/* Never unmounted: render JS writes this element's textContent and status classes via
            querySelector, so it must stay in the DOM. Its visibility is owned solely by
            applyMetricStatusVisibility (inline display, which wins over render JS classList
            writes) — React must not also set `display` here, or the two would race. */}
        <div
          className="metric-status text-card-muted-foreground font-semibold mt-1"
          style={{ fontSize: "clamp(0.4rem, 8cqi, 0.875rem)" }}
          data-sentry-mask
        ></div>
        <div
          className="metric-date text-card-muted-foreground mt-1 opacity-70"
          style={{ fontSize: "clamp(0.35rem, 7cqi, 0.75rem)" }}
        ></div>
      </div>
    </div>
  );
};
